import { Alert, Box, Button, Chip, CircularProgress, FormControl, FormLabel, Input, Option, Select, Sheet, Stack, Table, Typography } from '@mui/joy'
import { ClientDomainDNSMonitoring, DNSMonitoringStatus } from '@numeo/types-core'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { dnsMonitoringApi } from './api'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import SecurityIcon from '@mui/icons-material/Security'

interface GroupedApplication {
    projectName: string
    domains: ClientDomainDNSMonitoring[]
    healthyCount: number
    suspiciousCount: number
    errorCount: number
}

interface ExpandedState {
    [key: string]: boolean
}

export default function DNSMonitoringPage() {
    const [applications, setApplications] = useState<GroupedApplication[]>([])
    const [loading, setLoading] = useState(true)
    const [checking, setChecking] = useState(false)

    // Pagination
    const [page, setPage] = useState(1)
    const [pageSize] = useState(20)
    const [totalCount, setTotalCount] = useState(0)
    const [totalPages, setTotalPages] = useState(0)

    // Filters
    const [searchQuery, setSearchQuery] = useState('')
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('all')

    // Expand/collapse state
    const [expandedState, setExpandedState] = useState<ExpandedState>({})

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery)
        }, 500)
        return () => clearTimeout(timer)
    }, [searchQuery])

    // Reset page when filters change
    useEffect(() => {
        setPage(1)
    }, [debouncedSearchQuery, statusFilter])

    const loadApplications = useCallback(async () => {
        try {
            const filters: { projectName?: string; status?: DNSMonitoringStatus } = {}
            if (debouncedSearchQuery) {
                filters.projectName = debouncedSearchQuery
            }
            if (statusFilter !== 'all') {
                filters.status = statusFilter as DNSMonitoringStatus
            }

            const response = await dnsMonitoringApi.getAllApplicationsDNS(page, pageSize, filters)

            // Group by application
            const grouped: GroupedApplication[] = response.data.items.map((item) => {
                const healthyCount = item.results.filter((d) => d.status === DNSMonitoringStatus.HEALTHY).length
                const suspiciousCount = item.results.filter((d) => d.status === DNSMonitoringStatus.SUSPICIOUS).length
                const errorCount = item.results.filter((d) => d.status === DNSMonitoringStatus.ERROR).length

                return {
                    projectName: item.projectName,
                    domains: item.results,
                    healthyCount,
                    suspiciousCount,
                    errorCount,
                }
            })

            setApplications(grouped)
            setTotalCount(response.data.pagination.total)
            setTotalPages(response.data.pagination.totalPages)
        } catch (error) {
            console.error('Failed to load DNS monitoring:', error)
        } finally {
            setLoading(false)
        }
    }, [page, pageSize, debouncedSearchQuery, statusFilter])

    useEffect(() => {
        void loadApplications()
    }, [loadApplications])

    const handleCheckAll = async () => {
        try {
            setChecking(true)
            await dnsMonitoringApi.triggerCheckAll(30)
            await loadApplications()
        } catch (error) {
            console.error('Failed to trigger DNS check:', error)
        } finally {
            setChecking(false)
        }
    }

    const toggleApplicationExpand = (projectName: string) => {
        setExpandedState((prev) => ({
            ...prev,
            [projectName]: !(prev[projectName] || false),
        }))
    }

    const getStatusColor = (status: DNSMonitoringStatus): 'success' | 'warning' | 'danger' | 'neutral' => {
        switch (status) {
            case DNSMonitoringStatus.HEALTHY:
                return 'success'
            case DNSMonitoringStatus.SUSPICIOUS:
                return 'warning'
            case DNSMonitoringStatus.ERROR:
                return 'danger'
            default:
                return 'neutral'
        }
    }

    const totalSuspicious = applications.reduce((sum, app) => sum + app.suspiciousCount, 0)

    return (
        <Stack spacing={3} sx={{ p: 3 }}>
            <Box>
                <Typography level="h1" sx={{ mb: 1 }}>
                    <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '2rem' }} />
                    DNS Monitoring - All Applications
                </Typography>
                <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                    Monitor DNS records across all applications to detect potential security threats.
                </Typography>
            </Box>

            {totalSuspicious > 0 && (
                <Alert color="warning" variant="soft">
                    <Typography level="title-md">⚠️ {totalSuspicious} suspicious domains detected across all applications!</Typography>
                </Alert>
            )}

            {/* Filters */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
                <FormControl sx={{ flex: 1, minWidth: 200 }} size="sm">
                    <FormLabel>Search Application</FormLabel>
                    <Input
                        size="sm"
                        placeholder="Search by project name..."
                        startDecorator={<SearchIcon />}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </FormControl>

                <FormControl size="sm" sx={{ minWidth: 150 }}>
                    <FormLabel>Status Filter</FormLabel>
                    <Select size="sm" value={statusFilter} onChange={(e, value) => setStatusFilter(value as string)}>
                        <Option value="all">All</Option>
                        <Option value="healthy">Healthy Only</Option>
                        <Option value="suspicious">Suspicious Only</Option>
                        <Option value="error">Errors Only</Option>
                    </Select>
                </FormControl>

                <Button
                    onClick={handleCheckAll}
                    disabled={checking || loading}
                    size="sm"
                    startDecorator={checking ? <CircularProgress size="sm" /> : <RefreshIcon />}
                >
                    {checking ? 'Checking All...' : 'Check All DNS'}
                </Button>
            </Box>

            {/* Pagination Info */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography level="body-sm">
                    Showing {applications.length} of {totalCount} applications
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button size="sm" variant="outlined" disabled={page === 1 || loading} onClick={() => setPage(page - 1)}>
                        Previous
                    </Button>
                    <Typography level="body-sm">
                        Page {page} of {totalPages}
                    </Typography>
                    <Button size="sm" variant="outlined" disabled={page === totalPages || loading} onClick={() => setPage(page + 1)}>
                        Next
                    </Button>
                </Box>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : applications.length === 0 ? (
                <Alert color="neutral" variant="soft">
                    <Typography level="body-md">No applications found matching your filters.</Typography>
                </Alert>
            ) : (
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                    <Table stickyHeader hoverRow sx={{ '--TableCell-headBackground': 'var(--joy-palette-background-level1)', '--TableCell-paddingY': '12px' }}>
                        <thead>
                            <tr>
                                <th style={{ width: 40 }}></th>
                                <th style={{ width: '30%' }}>Application</th>
                                <th style={{ width: '20%' }}>Total Domains</th>
                                <th style={{ width: '15%' }}>Healthy</th>
                                <th style={{ width: '15%' }}>Suspicious</th>
                                <th style={{ width: '15%' }}>Errors</th>
                            </tr>
                        </thead>
                        <tbody>
                            {applications.map((app) => (
                                <Fragment key={app.projectName}>
                                    <tr key={app.projectName} onClick={() => toggleApplicationExpand(app.projectName)} style={{ cursor: 'pointer' }}>
                                        <td>
                                            <Button size="sm" variant="plain" color="neutral" sx={{ minHeight: 0, padding: 0, pointerEvents: 'none' }}>
                                                {expandedState[app.projectName] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                            </Button>
                                        </td>
                                        <td>
                                            <Typography level="title-sm" fontWeight="lg">
                                                {app.projectName}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-sm">{app.domains.length}</Typography>
                                        </td>
                                        <td>
                                            <Chip size="sm" color="success" variant="soft">
                                                {app.healthyCount}
                                            </Chip>
                                        </td>
                                        <td>
                                            {app.suspiciousCount > 0 ? (
                                                <Chip size="sm" color="warning">
                                                    {app.suspiciousCount}
                                                </Chip>
                                            ) : (
                                                <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                                                    0
                                                </Typography>
                                            )}
                                        </td>
                                        <td>
                                            {app.errorCount > 0 ? (
                                                <Chip size="sm" color="danger" variant="soft">
                                                    {app.errorCount}
                                                </Chip>
                                            ) : (
                                                <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                                                    0
                                                </Typography>
                                            )}
                                        </td>
                                    </tr>
                                    {expandedState[app.projectName] && (
                                        <tr>
                                            <td colSpan={6} style={{ backgroundColor: 'var(--joy-palette-background-level1)', padding: 0 }}>
                                                <Table size="sm" sx={{ '--TableCell-paddingY': '8px' }}>
                                                    <thead>
                                                        <tr>
                                                            <th>Domain</th>
                                                            <th>Email</th>
                                                            <th>Status</th>
                                                            <th>Last Checked</th>
                                                            <th>Changes</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {app.domains.map((domain) => (
                                                            <>
                                                                <tr key={domain.id}>
                                                                    <td>
                                                                        <Typography level="body-sm" fontWeight="md">
                                                                            {domain.domain}
                                                                        </Typography>
                                                                    </td>
                                                                    <td>
                                                                        <Typography level="body-sm">{domain.emails?.join(', ')}</Typography>
                                                                    </td>
                                                                    <td>
                                                                        <Chip size="sm" color={getStatusColor(domain.status)}>
                                                                            {domain.status}
                                                                        </Chip>
                                                                    </td>
                                                                    <td>
                                                                        <Typography level="body-xs">
                                                                            {domain.lastCheckedAt ? new Date(domain.lastCheckedAt).toLocaleString() : 'Never'}
                                                                        </Typography>
                                                                    </td>
                                                                    <td>
                                                                        {domain.suspiciousChanges && domain.suspiciousChanges.length > 0 ? (
                                                                            <Chip size="sm" color="warning" variant="soft">
                                                                                {domain.suspiciousChanges.length}
                                                                            </Chip>
                                                                        ) : (
                                                                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                                                -
                                                                            </Typography>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td colSpan={5} style={{ backgroundColor: 'var(--joy-palette-background-level2)' }}>
                                                                        <Box sx={{ p: 2 }}>
                                                                            {domain.errorMessage ? (
                                                                                <Alert color="danger" variant="soft" size="sm">
                                                                                    <Typography level="body-sm">{domain.errorMessage}</Typography>
                                                                                </Alert>
                                                                            ) : (
                                                                                <Stack spacing={2}>
                                                                                    {domain.suspiciousChanges && domain.suspiciousChanges.length > 0 && (
                                                                                        <Box>
                                                                                            <Typography level="title-sm" sx={{ mb: 1 }}>
                                                                                                Suspicious Changes:
                                                                                            </Typography>
                                                                                            <Stack spacing={1}>
                                                                                                {domain.suspiciousChanges.map((change, idx) => (
                                                                                                    <Alert key={idx} color="warning" variant="soft" size="sm">
                                                                                                        <Stack spacing={0.5}>
                                                                                                            <Typography level="body-xs">
                                                                                                                <strong>{change.field}</strong> changed on{' '}
                                                                                                                {new Date(change.changedAt).toLocaleString()}
                                                                                                            </Typography>
                                                                                                            <Typography level="body-xs" sx={{ pl: 1 }}>
                                                                                                                <strong>Old:</strong>{' '}
                                                                                                                {typeof change.oldValue === 'object'
                                                                                                                    ? JSON.stringify(change.oldValue)
                                                                                                                    : String(change.oldValue || 'N/A')}
                                                                                                            </Typography>
                                                                                                            <Typography level="body-xs" sx={{ pl: 1 }}>
                                                                                                                <strong>New:</strong>{' '}
                                                                                                                {typeof change.newValue === 'object'
                                                                                                                    ? JSON.stringify(change.newValue)
                                                                                                                    : String(change.newValue || 'N/A')}
                                                                                                            </Typography>
                                                                                                        </Stack>
                                                                                                    </Alert>
                                                                                                ))}
                                                                                            </Stack>
                                                                                        </Box>
                                                                                    )}

                                                                                    {domain.currentSnapshot && (
                                                                                        <Box>
                                                                                            <Typography level="title-sm" sx={{ mb: 1 }}>
                                                                                                Current DNS Records:
                                                                                            </Typography>
                                                                                            <Stack spacing={1} sx={{ pl: 2 }}>
                                                                                                <Box>
                                                                                                    <Typography level="body-xs" fontWeight="bold">
                                                                                                        SPF Records:
                                                                                                    </Typography>
                                                                                                    {domain.currentSnapshot.spf &&
                                                                                                    domain.currentSnapshot.spf.length > 0 ? (
                                                                                                        <Stack spacing={0.5} sx={{ pl: 1 }}>
                                                                                                            {domain.currentSnapshot.spf.map((spf, idx) => (
                                                                                                                <Typography
                                                                                                                    key={idx}
                                                                                                                    level="body-xs"
                                                                                                                    sx={{
                                                                                                                        fontFamily: 'monospace',
                                                                                                                        fontSize: '0.7rem',
                                                                                                                    }}
                                                                                                                >
                                                                                                                    {spf.value}
                                                                                                                </Typography>
                                                                                                            ))}
                                                                                                        </Stack>
                                                                                                    ) : (
                                                                                                        <Typography
                                                                                                            level="body-xs"
                                                                                                            sx={{ pl: 1, color: 'text.tertiary' }}
                                                                                                        >
                                                                                                            -
                                                                                                        </Typography>
                                                                                                    )}
                                                                                                </Box>

                                                                                                <Box>
                                                                                                    <Typography level="body-xs" fontWeight="bold">
                                                                                                        DMARC:
                                                                                                    </Typography>
                                                                                                    {domain.currentSnapshot.dmarc ? (
                                                                                                        <Typography
                                                                                                            level="body-xs"
                                                                                                            sx={{
                                                                                                                pl: 1,
                                                                                                                fontFamily: 'monospace',
                                                                                                                fontSize: '0.7rem',
                                                                                                            }}
                                                                                                        >
                                                                                                            Policy: {domain.currentSnapshot.dmarc.policy}
                                                                                                            {domain.currentSnapshot.dmarc.reportEmail &&
                                                                                                                ` | Report: ${domain.currentSnapshot.dmarc.reportEmail}`}
                                                                                                        </Typography>
                                                                                                    ) : (
                                                                                                        <Typography
                                                                                                            level="body-xs"
                                                                                                            sx={{ pl: 1, color: 'text.tertiary' }}
                                                                                                        >
                                                                                                            -
                                                                                                        </Typography>
                                                                                                    )}
                                                                                                </Box>

                                                                                                <Box>
                                                                                                    <Typography level="body-xs" fontWeight="bold">
                                                                                                        MX Records:
                                                                                                    </Typography>
                                                                                                    {domain.currentSnapshot.mx &&
                                                                                                    domain.currentSnapshot.mx.length > 0 ? (
                                                                                                        <Stack spacing={0.5} sx={{ pl: 1 }}>
                                                                                                            {domain.currentSnapshot.mx.map((mx, idx) => (
                                                                                                                <Typography
                                                                                                                    key={idx}
                                                                                                                    level="body-xs"
                                                                                                                    sx={{
                                                                                                                        fontFamily: 'monospace',
                                                                                                                        fontSize: '0.7rem',
                                                                                                                    }}
                                                                                                                >
                                                                                                                    {mx.priority} {mx.exchange}
                                                                                                                </Typography>
                                                                                                            ))}
                                                                                                        </Stack>
                                                                                                    ) : (
                                                                                                        <Typography
                                                                                                            level="body-xs"
                                                                                                            sx={{ pl: 1, color: 'text.tertiary' }}
                                                                                                        >
                                                                                                            -
                                                                                                        </Typography>
                                                                                                    )}
                                                                                                </Box>

                                                                                                <Box>
                                                                                                    <Typography level="body-xs" fontWeight="bold">
                                                                                                        NS Records:
                                                                                                    </Typography>
                                                                                                    {domain.currentSnapshot.ns &&
                                                                                                    domain.currentSnapshot.ns.length > 0 ? (
                                                                                                        <Stack spacing={0.5} sx={{ pl: 1 }}>
                                                                                                            {domain.currentSnapshot.ns.map((ns, idx) => (
                                                                                                                <Typography
                                                                                                                    key={idx}
                                                                                                                    level="body-xs"
                                                                                                                    sx={{
                                                                                                                        fontFamily: 'monospace',
                                                                                                                        fontSize: '0.7rem',
                                                                                                                    }}
                                                                                                                >
                                                                                                                    {ns.nameserver}
                                                                                                                </Typography>
                                                                                                            ))}
                                                                                                        </Stack>
                                                                                                    ) : (
                                                                                                        <Typography
                                                                                                            level="body-xs"
                                                                                                            sx={{ pl: 1, color: 'text.tertiary' }}
                                                                                                        >
                                                                                                            -
                                                                                                        </Typography>
                                                                                                    )}
                                                                                                </Box>

                                                                                                <Box>
                                                                                                    <Typography level="body-xs" fontWeight="bold">
                                                                                                        DKIM Records:
                                                                                                    </Typography>
                                                                                                    {domain.currentSnapshot.dkim &&
                                                                                                    domain.currentSnapshot.dkim.length > 0 ? (
                                                                                                        <Stack spacing={0.5} sx={{ pl: 1 }}>
                                                                                                            {domain.currentSnapshot.dkim.map((dkim, idx) => (
                                                                                                                <Typography
                                                                                                                    key={idx}
                                                                                                                    level="body-xs"
                                                                                                                    sx={{
                                                                                                                        fontFamily: 'monospace',
                                                                                                                        fontSize: '0.7rem',
                                                                                                                    }}
                                                                                                                >
                                                                                                                    Selector: {dkim.selector}
                                                                                                                </Typography>
                                                                                                            ))}
                                                                                                        </Stack>
                                                                                                    ) : (
                                                                                                        <Typography
                                                                                                            level="body-xs"
                                                                                                            sx={{ pl: 1, color: 'text.tertiary' }}
                                                                                                        >
                                                                                                            -
                                                                                                        </Typography>
                                                                                                    )}
                                                                                                </Box>

                                                                                                <Box>
                                                                                                    <Typography level="body-xs" fontWeight="bold">
                                                                                                        A Records - IPv4:
                                                                                                    </Typography>
                                                                                                    {domain.currentSnapshot.a &&
                                                                                                    domain.currentSnapshot.a.length > 0 ? (
                                                                                                        <Stack spacing={0.5} sx={{ pl: 1 }}>
                                                                                                            {domain.currentSnapshot.a.map((a, idx) => (
                                                                                                                <Typography
                                                                                                                    key={idx}
                                                                                                                    level="body-xs"
                                                                                                                    sx={{
                                                                                                                        fontFamily: 'monospace',
                                                                                                                        fontSize: '0.7rem',
                                                                                                                    }}
                                                                                                                >
                                                                                                                    {a.address}
                                                                                                                    {a.ttl && ` (TTL: ${a.ttl}s)`}
                                                                                                                </Typography>
                                                                                                            ))}
                                                                                                        </Stack>
                                                                                                    ) : (
                                                                                                        <Typography
                                                                                                            level="body-xs"
                                                                                                            sx={{ pl: 1, color: 'text.tertiary' }}
                                                                                                        >
                                                                                                            -
                                                                                                        </Typography>
                                                                                                    )}
                                                                                                </Box>
                                                                                            </Stack>
                                                                                        </Box>
                                                                                    )}
                                                                                </Stack>
                                                                            )}
                                                                        </Box>
                                                                    </td>
                                                                </tr>
                                                            </>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            ))}
                        </tbody>
                    </Table>
                </Sheet>
            )}

            {/* Bottom Pagination */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button size="sm" variant="outlined" disabled={page === 1 || loading} onClick={() => setPage(page - 1)}>
                    Previous
                </Button>
                <Typography level="body-sm" sx={{ alignSelf: 'center' }}>
                    Page {page} of {totalPages}
                </Typography>
                <Button size="sm" variant="outlined" disabled={page === totalPages || loading} onClick={() => setPage(page + 1)}>
                    Next
                </Button>
            </Box>
        </Stack>
    )
}
